import {
  Directive,
  HostListener,
  inject,
  HostAttributeToken,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import RouteUtils from '@core/utils/route.utils';
import { NEW_ENTITY_ROUTE_PARAM } from '@shared/constants';
import { AgGridAngular } from 'ag-grid-angular';
import {
  FilterChangedEvent,
  IRowNode,
  ModelUpdatedEvent,
} from 'ag-grid-community';

/** selects same row by id or the first one after grid refresh
 * for client side grids that are on the left side of the sidesheet.
 * Deselects sidesheets grids (client side and server side) when selected entity changes.
 */
@Directive({
  selector: 'ag-grid-angular',
  standalone: false,
})
export class GridRowSelectionDirective {
  private activatedRoute = inject(ActivatedRoute);
  private agGrid = inject(AgGridAngular);

  private isGridWithSidesheet;
  private isGridInSidesheet: boolean;

  constructor() {
    const vtGridWithSidesheet = inject(
      new HostAttributeToken('vtGridWithSidesheet'),
      { optional: true }
    )!;
    const dvGridInSidesheet = inject(
      new HostAttributeToken('dvGridInSidesheet'),
      { optional: true }
    )!;

    this.isGridWithSidesheet = vtGridWithSidesheet === '';
    this.isGridInSidesheet = dvGridInSidesheet === '';
  }

  @HostListener('modelUpdated', ['$event'])
  onModelUpdated(evt: Event): void {
    const event = evt as unknown as ModelUpdatedEvent;
    this.preventSidesheetFromClosing(event);
  }

  @HostListener('filterChanged', ['$event'])
  onFilterChanged(evt: Event): void {
    const event = evt as unknown as FilterChangedEvent;
    this.deselectNodesInSidesheetGrid(event);
  }

  private preventSidesheetFromClosing(event: ModelUpdatedEvent): void {
    if (
      !this.isGridWithSidesheet ||
      (event.api.getGridOption('rowModelType') !== 'clientSide' &&
        event.api.getSelectedNodes().length > 1)
    )
      return;

    const entityId = RouteUtils.getEntityId(this.activatedRoute.firstChild);
    const selectedNode = event.api.getSelectedNodes()[0] as
      | IRowNode
      | undefined;

    if (
      entityId &&
      selectedNode?.id &&
      selectedNode.id !== entityId &&
      entityId !== NEW_ENTITY_ROUTE_PARAM &&
      event.api.getDisplayedRowCount() > 0
    ) {
      const nodeToSelect = event.api.getRowNode(entityId);
      if (nodeToSelect && nodeToSelect.id) {
        nodeToSelect.setSelected(true, true);
      } else {
        const firstNodeToSelect = event.api.getDisplayedRowAtIndex(0);
        // making sure node has ID (in case it's loading node indicator)
        if (firstNodeToSelect?.id) {
          firstNodeToSelect.setSelected(true, true);
        }
      }
    }
  }

  private deselectNodesInSidesheetGrid(event: FilterChangedEvent): void {
    const mode = this.agGrid.gridOptions?.rowSelection;
    const isSingleRowSelection =
      (typeof mode === 'object' ? mode.mode === 'singleRow' : mode) ===
      'single';

    if (
      event.source === 'api' &&
      this.isGridInSidesheet &&
      isSingleRowSelection &&
      event.api.getSelectedNodes()[0]
    ) {
      event.api.deselectAll();
      event.api.clearFocusedCell();
    }
  }
}
